import type { EntryType } from "ls/features/settings/types";
import type { RequestOptions } from "../common/server";
import type { ListJs } from "@/common/models";
import type { ItemType, NotificationJs, NotificationMethod, NotificationMuteItemJs, NotificationSettingJs, ResponseObject } from "@/generated/models";
import { objectToFormData } from "@/services/utils/formData";
import { server } from "./server";

export async function getNotificationSettings({ signal }: RequestOptions = {}) {
  const response = await server.get("notification/getNotificationSettings", {
    signal,
  });
  return await response.json() as ListJs<NotificationSettingJs>;
}

export async function saveNotificationSettings(model: NotificationSettingJs) {
  const formData = objectToFormData(model);
  const response = await server.post("notification/saveNotificationSettings", { body: formData });
  return await response.json() as NotificationSettingJs;
}

export async function getUserNotifications({ signal }: RequestOptions = {}) {
  const response = await server.get("notification/getUserSubscriptions", {
    signal,
  });
  return await response.json() as ListJs<NotificationJs>;
}

export interface AddNotificationModel {
  subscriptionRate: "Instant";
  targetId?: string;
  targetType: EntryType;
  isSlack: boolean;
  msTeamsUrl?: string;
  isMsTeams?: boolean;
}

export async function addNotification(model: AddNotificationModel) {
  const formData = objectToFormData(model);

  const response = await server.post("notification/addsubscription", { body: formData });

  if (model.isSlack) {
    const responseObject = await response.json() as ResponseObject;
    if (responseObject.redirecturl) {
      window.location.href = responseObject.redirecturl;
      return;
    }
  }
  return await response.json() as NotificationJs;
}

export interface EditNotificationModel {
  notificationId: string;
  newTargetId?: string;
  newTargetType: EntryType;
  newSubRate: "Instant";
  newMsTeamsUrl?: string;
}
export async function editNotification(model: EditNotificationModel) {
  const formData = objectToFormData(model);
  const response = await server.post("notification/editSubscription", { body: formData });
  return await response.json() as NotificationJs;
}

export async function getVisitorNotifications({ signal }: RequestOptions = {}) {
  const response = await server.get("notification/getUserVisitorSubscriptions", {
    signal,
  });
  return await response.json() as ListJs<NotificationJs>;
}

export async function deleteNotification(notificationId: string) {
  const formData = objectToFormData({ notificationId });

  const response = await server.post("notification/deleteSubscription", { body: formData });
  return await response.json() as NotificationJs;
}

export async function getMuteNotifications({ signal }: RequestOptions = {}) {
  const response = await server.get("notification/getNotificationMuteItems", {
    signal,
  });
  return await response.json() as ListJs<NotificationMuteItemJs>;
}

export interface NotificationMuteItemModel {
  notificationType: NotificationMethod;
  targetId: string;
  targetType: ItemType;
}
export async function addMuteNotification(model: NotificationMuteItemModel) {
  const formData = objectToFormData(model);
  const response = await server.post("notification/addNotificationMuteItem", { body: formData });
  return await response.json() as NotificationMuteItemJs;
}

export async function deleteMuteNotification(notificationMuteItemId: string) {
  const formData = objectToFormData({ notificationMuteItemId });
  const response = await server.post("notification/deleteNotificationMuteItem", { body: formData });
  return await response.json() as NotificationJs;
}

export async function changeSlackChannel(notificationId: string) {
  const formData = objectToFormData({ notificationId });
  const response = await server.post("notification/changeSlackChannel", { body: formData });

  const responseObject = await response.json() as ResponseObject;

  if (responseObject.redirecturl) {
    window.location.href = responseObject.redirecturl;
    return;
  }

  return responseObject;
}
